import { useEffect } from 'react';
import type { MapRef } from 'react-map-gl';

/** The fill whose opacity this pulses. Exported so the test names the same layer. */
export const OVERLAP_FILL_LAYER_ID = 'tc-dev-area-overlap-fill';

/** One full 1.0 → 0.45 → 1.0 cycle. */
const PERIOD_MS = 1400;

/**
 * Whether the map still has a style to paint onto.
 *
 * `map.remove()` sets `style` to undefined permanently (`setStyle(null)` skips the
 * re-init branch), and mapbox's `getLayer` is `return this.style.getLayer(id)` with no
 * guard of its own — so on a torn-down map `getLayer` is the thing that throws
 * `Cannot read properties of undefined (reading 'getLayer')`.
 *
 * Deliberately NOT `isStyleLoaded()`, which is `this.style.loaded()`: that also reports
 * false while any source is still fetching tiles or images, so using it would freeze the
 * pulse mid-cycle on every tile load and skip the reset-to-1 when the overlaps clear
 * during a pan or zoom. Existence is the only thing this guard needs to know.
 * @param {mapboxGl.Map} map The map to probe.
 * @return {boolean} True while the style object is still there.
 */
const hasStyle = (map: ReturnType<MapRef['getMap']>): boolean =>
  !!(map as unknown as { style?: unknown }).style;

/**
 * Pulse the red overlap fill so conflicting units are impossible to miss.
 *
 * MapboxGL has no per-feature DOM node, so the only way to animate a feature is to drive
 * the layer's paint opacity frame by frame.
 *
 * Lives in its own module rather than inline in `tc-development-areas-layer` so the
 * teardown behaviour below can be tested without mounting a 1,900-line layer component.
 * @param {MapRef | null} mapRef Handle on the map that owns the overlap layer, once it
 * has loaded — the same shape the other map hooks take.
 * @param {boolean} active Whether to pulse — an active workspace with overlapping units.
 */
export const useOverlapPulse = (
  mapRef: MapRef | null,
  active: boolean
): void => {
  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map || !active) return;

    let raf = 0;
    let startTs = 0;

    /**
     * Paint one frame's opacity, if there is still a style to paint onto.
     * @param {number} opacity The fill-opacity to set.
     */
    const paint = (opacity: number) => {
      if (!hasStyle(map)) return;
      if (!map.getLayer(OVERLAP_FILL_LAYER_ID)) return;

      map.setPaintProperty(OVERLAP_FILL_LAYER_ID, 'fill-opacity', opacity);
    };

    const animate = (ts: number) => {
      if (!startTs) startTs = ts;
      const phase = ((ts - startTs) % PERIOD_MS) / PERIOD_MS;
      const eased = 0.5 - 0.5 * Math.cos(2 * Math.PI * phase); // 0→1→0
      paint(1 - 0.55 * eased); // 1.0 → 0.45 → 1.0
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(raf);
      // Resetting to full opacity matters when this effect merely re-runs (the overlaps
      // cleared while the map stayed alive). On UNMOUNT there is nothing to reset and the
      // map is already gone: React destroys a deleted subtree parent-first, so
      // react-map-gl's <Map> cleanup has run `map.remove()` before this fires. `paint`
      // no-ops in that case rather than throwing, which is the crash this guards.
      paint(1);
    };
  }, [mapRef, active]);
};
